import type {
  ConversionAnalyticsConfig,
  ConversionAnalyticsInitializationState,
} from "../Conversion/types";

export type { ConversionAnalyticsConfig } from "../Conversion/types";

type ConversionAnalyticsProvider = "gtm" | "ga4" | "meta";
type ConversionAnalyticsPlatform = "web" | "native";
type Gtag = (...args: unknown[]) => void;
type Fbq = {
  (...args: unknown[]): void;
  loaded?: boolean;
  version?: string;
  queue?: unknown[][];
  push?: Fbq;
};

export type ConversionAnalyticsWindow = {
  dataLayer?: unknown[];
  gtag?: Gtag;
  fbq?: Fbq;
  __whooshConversionAnalytics?: ConversionAnalyticsInitializationState;
};

export type ConversionAnalyticsScriptLoader = (
  provider: ConversionAnalyticsProvider,
  providerId: string,
  scriptUrl: string
) => void;

export type BootstrapConversionAnalyticsOptions = {
  platform?: ConversionAnalyticsPlatform;
  targetWindow?: ConversionAnalyticsWindow;
  targetDocument?: Document;
  loadScript?: ConversionAnalyticsScriptLoader;
  scopeId?: string | null;
  analyticsTrackingAllowed?: boolean;
};

const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GA4_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/;
const META_PIXEL_ID_PATTERN = /^\d{5,30}$/;

const getNormalizedId = (
  value: unknown,
  pattern: RegExp,
  uppercase: boolean
): string | null | undefined => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim();
  const normalizedId = uppercase
    ? normalizedValue.toUpperCase()
    : normalizedValue;

  return pattern.test(normalizedId) ? normalizedId : undefined;
};

const normalizeConfig = (
  config: ConversionAnalyticsConfig | null | undefined
) => {
  if (!config || config.enabled !== true) {
    return null;
  }

  const gtmContainerId = getNormalizedId(
    config.gtmContainerId,
    GTM_CONTAINER_ID_PATTERN,
    true
  );
  const ga4MeasurementId = getNormalizedId(
    config.ga4MeasurementId,
    GA4_MEASUREMENT_ID_PATTERN,
    true
  );
  const metaPixelId = getNormalizedId(
    config.metaPixelId,
    META_PIXEL_ID_PATTERN,
    false
  );

  if (
    gtmContainerId === undefined ||
    ga4MeasurementId === undefined ||
    metaPixelId === undefined ||
    (!gtmContainerId && !ga4MeasurementId && !metaPixelId)
  ) {
    return null;
  }

  return {
    gtmContainerId,
    ga4MeasurementId,
    metaPixelId,
  };
};

const getInitializationState = (
  targetWindow: ConversionAnalyticsWindow
): ConversionAnalyticsInitializationState => {
  if (!targetWindow.__whooshConversionAnalytics) {
    targetWindow.__whooshConversionAnalytics = {
      gtmContainerIds: new Set<string>(),
      ga4MeasurementIds: new Set<string>(),
      metaPixelIds: new Set<string>(),
      seenEventIds: new Set<string>(),
    };
  }

  targetWindow.__whooshConversionAnalytics.seenEventIds ??= new Set<string>();
  targetWindow.__whooshConversionAnalytics.dataLayers ??= {};

  return targetWindow.__whooshConversionAnalytics;
};

const ensureDataLayer = (
  targetWindow: ConversionAnalyticsWindow
): unknown[] => {
  if (!Array.isArray(targetWindow.dataLayer)) {
    targetWindow.dataLayer = [];
  }

  return targetWindow.dataLayer;
};

const getDataLayerName = (scopeId: string | null | undefined): string => {
  if (!scopeId) return "dataLayer";

  const encodedScopeId = Array.from(scopeId)
    .map((character) => character.codePointAt(0)?.toString(16).padStart(4, "0"))
    .join("");

  return `__whooshConversionDataLayer_${encodedScopeId}`;
};

const ensureScopedDataLayer = (
  targetWindow: ConversionAnalyticsWindow,
  state: ConversionAnalyticsInitializationState,
  scopeId: string | null | undefined
): { dataLayer: unknown[]; dataLayerName: string } => {
  const dataLayerName = getDataLayerName(scopeId);
  const dataLayers = state.dataLayers ?? {};
  const dataLayer =
    dataLayers[dataLayerName] ??
    (dataLayerName === "dataLayer" ? ensureDataLayer(targetWindow) : []);

  dataLayers[dataLayerName] = dataLayer;
  state.dataLayers = dataLayers;
  state.activeScopeId = scopeId;
  state.activeDataLayerName = dataLayerName;
  state.activeDataLayer = dataLayer;

  if (dataLayerName !== "dataLayer") {
    Object.defineProperty(targetWindow, dataLayerName, {
      configurable: true,
      enumerable: false,
      value: dataLayer,
      writable: true,
    });
  }

  return { dataLayer, dataLayerName };
};

const createGtag = (dataLayer: unknown[]): Gtag => {
  return function gtag() {
    dataLayer.push(arguments);
  };
};

const createFbq = (): Fbq => {
  const fbq: Fbq = (...args: unknown[]) => {
    fbq.queue?.push(args);
  };

  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  fbq.push = fbq;

  return fbq;
};

const loadBrowserScript = (
  targetDocument: Document,
  provider: ConversionAnalyticsProvider,
  providerId: string,
  scriptUrl: string
) => {
  const existingScript = Array.from(
    targetDocument.getElementsByTagName("script")
  ).find((script) => script.src === scriptUrl);

  if (existingScript) {
    return;
  }

  const script = targetDocument.createElement("script");
  script.async = true;
  script.src = scriptUrl;
  script.dataset.whooshProvider = provider;
  script.dataset.whooshProviderId = providerId;
  targetDocument.head.appendChild(script);
};

const clearProviderState = (
  targetWindow: ConversionAnalyticsWindow,
  targetDocument: Document | undefined,
  state: ConversionAnalyticsInitializationState
): void => {
  if (targetDocument) {
    Array.from(
      targetDocument.querySelectorAll("script[data-whoosh-provider]")
    ).forEach((script) => script.remove());
  }

  const hadInitializedProvider =
    state.conversionAnalyticsConfig !== undefined ||
    state.gtmContainerIds.size > 0 ||
    state.ga4MeasurementIds.size > 0 ||
    state.metaPixelIds.size > 0;

  if (hadInitializedProvider) {
    targetWindow.gtag = undefined;
    targetWindow.fbq = undefined;
  }

  for (const dataLayerName of Object.keys(state.dataLayers ?? {})) {
    if (dataLayerName !== "dataLayer") {
      Reflect.deleteProperty(targetWindow, dataLayerName);
    }
  }

  state.gtmContainerIds.clear();
  state.ga4MeasurementIds.clear();
  state.metaPixelIds.clear();
  state.dataLayers = {};
  state.gtagDataLayerName = undefined;
};

const loadGtm = (
  containerId: string,
  dataLayer: unknown[],
  dataLayerName: string,
  state: ConversionAnalyticsInitializationState,
  loadScript: ConversionAnalyticsScriptLoader
) => {
  const providerKey = `${dataLayerName}:${containerId}`;

  if (state.gtmContainerIds.has(providerKey)) {
    return;
  }

  state.gtmContainerIds.add(providerKey);
  dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const dataLayerQuery =
    dataLayerName === "dataLayer"
      ? ""
      : `&l=${encodeURIComponent(dataLayerName)}`;
  loadScript(
    "gtm",
    containerId,
    `https://www.googletagmanager.com/gtm.js?id=${containerId}${dataLayerQuery}`
  );
};

const loadGa4 = (
  targetWindow: ConversionAnalyticsWindow,
  measurementId: string,
  dataLayer: unknown[],
  dataLayerName: string,
  state: ConversionAnalyticsInitializationState,
  loadScript: ConversionAnalyticsScriptLoader
) => {
  const gtag =
    state.gtagDataLayerName === dataLayerName && targetWindow.gtag
      ? targetWindow.gtag
      : createGtag(dataLayer);
  targetWindow.gtag = gtag;
  state.gtagDataLayerName = dataLayerName;

  const providerKey = `${dataLayerName}:${measurementId}`;

  if (state.ga4MeasurementIds.has(providerKey)) {
    return;
  }

  state.ga4MeasurementIds.add(providerKey);
  gtag("js", new Date());
  gtag("config", measurementId);
  const dataLayerQuery =
    dataLayerName === "dataLayer"
      ? ""
      : `&l=${encodeURIComponent(dataLayerName)}`;
  loadScript(
    "ga4",
    measurementId,
    `https://www.googletagmanager.com/gtag/js?id=${measurementId}${dataLayerQuery}`
  );
};

const loadMeta = (
  targetWindow: ConversionAnalyticsWindow,
  pixelId: string,
  dataLayerName: string,
  state: ConversionAnalyticsInitializationState,
  loadScript: ConversionAnalyticsScriptLoader
) => {
  const fbq = targetWindow.fbq ?? createFbq();
  targetWindow.fbq = fbq;

  const providerKey = `${dataLayerName}:${pixelId}`;

  if (state.metaPixelIds.has(providerKey)) {
    return;
  }

  state.metaPixelIds.add(providerKey);
  fbq("init", pixelId);
  fbq("trackSingle", pixelId, "PageView");
  loadScript("meta", pixelId, "https://connect.facebook.net/en_US/fbevents.js");
};

const clearRemovedProviderState = (
  targetWindow: ConversionAnalyticsWindow,
  targetDocument: Document | undefined,
  state: ConversionAnalyticsInitializationState,
  normalizedConfig: {
    gtmContainerId: string | null;
    ga4MeasurementId: string | null;
    metaPixelId: string | null;
  }
): void => {
  const clearProvider = (
    provider: ConversionAnalyticsProvider,
    providerIds: Set<string>,
    activeProviderId: string | null
  ) => {
    for (const providerKey of providerIds) {
      const providerId = providerKey.slice(providerKey.lastIndexOf(":") + 1);

      if (providerId !== activeProviderId) {
        providerIds.delete(providerKey);
      }
    }

    if (!targetDocument) {
      return;
    }

    Array.from(
      targetDocument.querySelectorAll<HTMLScriptElement>(
        "script[data-whoosh-provider]"
      )
    )
      .filter(
        (script) =>
          script.dataset.whooshProvider === provider &&
          script.dataset.whooshProviderId !== activeProviderId
      )
      .forEach((script) => script.remove());
  };

  clearProvider("gtm", state.gtmContainerIds, normalizedConfig.gtmContainerId);
  clearProvider(
    "ga4",
    state.ga4MeasurementIds,
    normalizedConfig.ga4MeasurementId
  );
  clearProvider("meta", state.metaPixelIds, normalizedConfig.metaPixelId);

  if (!normalizedConfig.ga4MeasurementId) {
    targetWindow.gtag = undefined;
    state.gtagDataLayerName = undefined;
  }

  if (!normalizedConfig.metaPixelId) {
    targetWindow.fbq = undefined;
  }
};

export const bootstrapConversionAnalytics = (
  config: ConversionAnalyticsConfig | null | undefined,
  options: BootstrapConversionAnalyticsOptions = {}
): void => {
  if (options.platform === "native") {
    return;
  }

  const targetWindow =
    options.targetWindow ??
    (typeof window === "undefined" ? undefined : window);
  if (!targetWindow) {
    return;
  }

  const state = getInitializationState(targetWindow);
  const targetDocument =
    options.targetDocument ??
    (typeof document === "undefined" ? undefined : document);
  const normalizedConfig =
    options.analyticsTrackingAllowed === false ? null : normalizeConfig(config);
  state.conversionAnalyticsConfig = normalizedConfig
    ? {
        enabled: true,
        ...(normalizedConfig.gtmContainerId
          ? { gtmContainerId: normalizedConfig.gtmContainerId }
          : {}),
        ...(normalizedConfig.ga4MeasurementId
          ? { ga4MeasurementId: normalizedConfig.ga4MeasurementId }
          : {}),
        ...(normalizedConfig.metaPixelId
          ? { metaPixelId: normalizedConfig.metaPixelId }
          : {}),
      }
    : undefined;

  if (!normalizedConfig) {
    clearProviderState(targetWindow, targetDocument, state);
    state.activeScopeId = options.scopeId;
    state.activeDataLayerName = getDataLayerName(options.scopeId);
    state.activeDataLayer = undefined;
    return;
  }

  clearRemovedProviderState(
    targetWindow,
    targetDocument,
    state,
    normalizedConfig
  );

  const { dataLayer, dataLayerName } = ensureScopedDataLayer(
    targetWindow,
    state,
    options.scopeId
  );

  const loadScript =
    options.loadScript ??
    (targetDocument
      ? (provider, providerId, scriptUrl) =>
          loadBrowserScript(targetDocument, provider, providerId, scriptUrl)
      : undefined);

  if (!loadScript) {
    return;
  }

  if (normalizedConfig.gtmContainerId) {
    loadGtm(
      normalizedConfig.gtmContainerId,
      dataLayer,
      dataLayerName,
      state,
      loadScript
    );
  }

  if (normalizedConfig.ga4MeasurementId) {
    loadGa4(
      targetWindow,
      normalizedConfig.ga4MeasurementId,
      dataLayer,
      dataLayerName,
      state,
      loadScript
    );
  }

  if (normalizedConfig.metaPixelId) {
    loadMeta(
      targetWindow,
      normalizedConfig.metaPixelId,
      dataLayerName,
      state,
      loadScript
    );
  }
};
